import React, { useState, useEffect } from 'react';
import farmAuthService from '../services/farmAuthService';

const SuperAdminDashboard = ({ onBackToLogin }) => {
  const [activeTab, setActiveTab] = useState('farms');
  const [farms, setFarms] = useState([]);
  const [invitationCodes, setInvitationCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Generate new invitation code for farm registration
  const generateInvitationCode = () => {
    const newCode = farmAuthService.generateInvitationCode();
    const newInvitation = {
      code: newCode,
      createdAt: new Date().toISOString(),
      usedAt: null,
      farmId: null,
      isActive: true
    };

    const currentCodes = JSON.parse(localStorage.getItem('invitationCodes') || '[]');
    currentCodes.push(newInvitation);
    localStorage.setItem('invitationCodes', JSON.stringify(currentCodes));
    
    // Also add to valid codes
    const validCodes = farmAuthService.getValidInvitationCodes();
    validCodes.push(newCode);
    localStorage.setItem('validInvitationCodes', JSON.stringify(validCodes));
    
    loadInvitationCodes();
  };

  const loadFarms = () => {
    const allFarms = JSON.parse(localStorage.getItem('farms') || '[]');
    setFarms(allFarms);
  };

  const loadInvitationCodes = () => {
    const codes = JSON.parse(localStorage.getItem('invitationCodes') || '[]');
    setInvitationCodes(codes);
  };

  const deactivateFarm = (farmId) => {
    if (window.confirm('Are you sure you want to deactivate this farm?')) {
      const allFarms = JSON.parse(localStorage.getItem('farms') || '[]');
      const updatedFarms = allFarms.map(farm => 
        farm.id === farmId ? { ...farm, isActive: false } : farm
      );
      localStorage.setItem('farms', JSON.stringify(updatedFarms));
      loadFarms();
    }
  };

  const resetFarmPassword = (farmId) => {
    const users = JSON.parse(localStorage.getItem('users') || '[]');
    const manager = users.find(u => u.farmId === farmId && u.role === 'manager');
    
    if (manager) {
      const newTempPassword = farmAuthService.generateTemporaryPassword();
      manager.temporaryPassword = newTempPassword;
      manager.mustChangePassword = true;
      
      const updatedUsers = users.map(u => u.id === manager.id ? manager : u);
      localStorage.setItem('users', JSON.stringify(updatedUsers));
      
      alert(`New temporary password for ${manager.name}: ${newTempPassword}`);
    }
  };

  useEffect(() => {
    loadFarms();
    loadInvitationCodes();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Super Admin Dashboard</h1>
            <button
              onClick={onBackToLogin}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('farms')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'farms'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Farms ({farms.length})
            </button>
            <button
              onClick={() => setActiveTab('invitations')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'invitations'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Invitation Codes ({invitationCodes.filter(c => c.isActive).length})
            </button>
          </nav>
        </div>

        {/* Farms Tab */}
        {activeTab === 'farms' && (
          <div>
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Registered Farms</h2>
              </div>
              
              {farms.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  No farms registered yet
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Farm Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Location
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {farms.map((farm) => (
                        <tr key={farm.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{farm.name}</div>
                            <div className="text-sm text-gray-500">{farm.id}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {farm.settings?.location || 'Not specified'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(farm.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              farm.isActive !== false 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {farm.isActive !== false ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            <button
                              onClick={() => resetFarmPassword(farm.id)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Reset Password
                            </button>
                            <button
                              onClick={() => deactivateFarm(farm.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Deactivate
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Invitation Codes Tab */}
        {activeTab === 'invitations' && (
          <div>
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">Invitation Codes</h2>
                <button
                  onClick={generateInvitationCode}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Generate New Code
                </button>
              </div>
              
              {invitationCodes.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  No invitation codes generated yet
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Code
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Used By
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invitationCodes.map((invitation, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-mono font-medium text-gray-900">
                              {invitation.code}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(invitation.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              invitation.isActive && !invitation.usedAt
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {invitation.isActive && !invitation.usedAt ? 'Available' : 'Used'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {invitation.farmId ? (
                              farms.find(f => f.id === invitation.farmId)?.name || 'Unknown Farm'
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">How to onboard a new farm:</h3>
              <ol className="text-sm text-blue-700 space-y-1">
                <li>1. Generate a new invitation code above</li>
                <li>2. Send the code to the farm manager</li>
                <li>3. They use it to register at your domain/register</li>
                <li>4. They create their farm and manager account</li>
                <li>5. Manager can then create worker/accountant accounts</li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminDashboard; 